import React from "react"
import clsx from "clsx"
import moment from "moment"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"
import { useNavigate, useParams } from "react-router-dom"

import MainLayout from "@components/layout/MainLayout/MainLayout"
import EntityPageHeaderLayout from "@components/layout/EntityPageHeaderLayout/EntityPageHeaderLayout"
import BackButton from "@components/prototypes/BackButton"
import Button from "@components/ui/Button/Button"
import Loader from "@components/ui/Loader/BarLoader"
import NotFound from "@components/ui/NotFound/NotFound"
import Text from "@components/ui/Typography/Text"
import Icon from "@components/ui/Icon/Icon"
import useDebounced from "@components/hooks/useDebounced"
import mainRoutes from "@root/main.routes"
import { useStore } from "@store/index"
import {
  EmailKnowledgeStatus,
  EmailKnowledgeSubmission,
} from "@services/email-knowledge.service"

import styles from "./EmailKnowledgePage.module.sass"

type ParamsType = {
  submissionId?: string
}

const STATUS_FILTERS: Array<EmailKnowledgeStatus | "all"> = [
  "pending",
  "approved",
  "rejected",
  "archived",
  "all",
]

const statusIcon = (status: EmailKnowledgeStatus) => {
  if (status === "approved")
    return { name: "shield-check-fill" as const, color: "green" as const }
  if (status === "rejected")
    return { name: "cross" as const, color: "red" as const }
  if (status === "archived")
    return { name: "loader" as const, color: "default" as const }
  return { name: "time" as const, color: "gold" as const }
}

const EmailKnowledgePage: React.FC = observer(() => {
  const alert = useAlert()
  const navigate = useNavigate()
  const { submissionId } = useParams<ParamsType>()

  const {
    emailKnowledgeStore: store,
    restrictionsStore: access,
  } = useStore()

  const [subject, setSubject] = React.useState("")
  const [bodyText, setBodyText] = React.useState("")
  const [brand, setBrand] = React.useState("")
  const [reviewNote, setReviewNote] = React.useState("")
  const [search, setSearch] = React.useState("")

  const debouncedReload = useDebounced(() => store.reload(), 400)

  React.useEffect(() => {
    store.load()
  }, [store.reloadTrigger, store.statusFilter])

  React.useEffect(() => {
    if (submissionId) {
      store.setSelectedId(submissionId)
    }
  }, [submissionId])

  React.useEffect(() => {
    if (store.selectedId && store.selectedId !== submissionId) {
      navigate(mainRoutes.emailKnowledgeItem(store.selectedId), { replace: true })
    }
  }, [store.selectedId])

  React.useEffect(() => {
    const { selected: current } = store
    if (!current) return
    setSubject(current.subject || "")
    setBodyText(current.bodyText || "")
    setBrand(current.brand || "")
    setReviewNote(current.reviewNote || "")
  }, [store.selected?.id, store.selected?.updatedAt])

  const handleSelect = (id: string) => {
    store.setSelectedId(id)
    navigate(mainRoutes.emailKnowledgeItem(id), { replace: true })
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    store.setQuery(value)
    debouncedReload()
  }

  const ensurePending = (item: EmailKnowledgeSubmission | null) => {
    if (!item) return false
    if (item.status !== "pending") {
      alert.error("Only pending submissions can be changed")
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!ensurePending(store.selected) || !store.selected) return
    const ok = await store.update(store.selected.id, {
      subject,
      bodyText,
      brand,
      reviewNote,
    })
    if (ok) alert.success("Submission saved")
    else alert.error(store.error || "Save failed")
  }

  const handleApprove = async () => {
    if (!ensurePending(store.selected) || !store.selected) return
    if (!access.canCreateExpertArticleInsights) {
      alert.error("You do not have permission to approve insights")
      return
    }
    const ok = await store.approve(store.selected.id, {
      subject,
      bodyText,
      brand,
      reviewNote,
    })
    if (ok) {
      alert.success("Approved and indexed in LabWizard")
      store.reload()
    } else alert.error(store.error || "Approve failed")
  }

  const handleReject = async () => {
    if (!ensurePending(store.selected) || !store.selected) return
    if (!access.canUpdateExpertArticleInsights) {
      alert.error("You do not have permission to reject submissions")
      return
    }
    const ok = await store.reject(store.selected.id, reviewNote)
    if (ok) {
      alert.success("Submission rejected")
      store.reload()
    } else alert.error(store.error || "Reject failed")
  }

  const handleArchive = async () => {
    if (!store.selected) return
    if (!access.canUpdateExpertArticleInsights) {
      alert.error("You do not have permission to archive submissions")
      return
    }
    const ok = await store.archive(store.selected.id)
    if (ok) {
      alert.success("Submission archived")
      store.reload()
    } else alert.error(store.error || "Archive failed")
  }

  const { selected } = store
  const isPending = selected?.status === "pending"

  return (
    <MainLayout>
      <div className={styles.root}>
        <EntityPageHeaderLayout
          className={styles.header}
          left={
            <BackButton
              size="medium"
              onClick={() => navigate(mainRoutes.expertsMenu())}
            >
              Email Knowledge
            </BackButton>
          }
        >
          <input
            className={styles.input}
            placeholder="Search by subject…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </EntityPageHeaderLayout>

        <div className={styles.filters}>
          {STATUS_FILTERS.map((status) => (
            <Button
              key={status}
              size="small"
              variant={store.statusFilter === status ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                store.setStatusFilter(status)
                store.reload()
              }}
            >
              {status}
            </Button>
          ))}
        </div>

        {!store.items.length ? (
          store.isLoading ? (
            <Loader size="huge" fluid />
          ) : (
            <NotFound>No email knowledge submissions found</NotFound>
          )
        ) : (
          <div className={styles.body}>
            <div className={styles.left}>
              <div className={styles.list}>
                {store.items.map((item) => {
                  const badge = statusIcon(item.status)
                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={clsx(
                        styles.card,
                        item.id === selected?.id && styles.active
                      )}
                      onClick={() => handleSelect(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          handleSelect(item.id)
                        }
                      }}
                    >
                      <div className={styles.cardMeta}>
                        <Text variant="h5" className={styles.badge}>
                          <Icon name={badge.name} color={badge.color} inline />{" "}
                          {item.status}
                        </Text>
                        <Text variant="caption2" color="text50Color">
                          {moment(item.createdAt).fromNow()}
                        </Text>
                      </div>
                      <Text variant="h4" weight="bold">
                        {item.subject}
                      </Text>
                      <Text variant="caption2" color="text50Color">
                        {item.fromAddress}
                        {item.brand ? ` · ${item.brand}` : ""}
                      </Text>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className={styles.right}>
              {store.isLoading || store.isActionLoading ? (
                <Loader size="huge" fluid />
              ) : selected ? (
                <div className={styles.detail}>
                  <Text variant="h3">{selected.subject}</Text>
                  <Text variant="caption1" color="text50Color">
                    From {selected.fromAddress} → {selected.toAddress}
                  </Text>

                  <div className={styles.field}>
                    <Text variant="h5">Subject / article title</Text>
                    <input
                      className={styles.input}
                      value={subject}
                      disabled={!isPending}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <Text variant="h5">Brand / topic</Text>
                    <input
                      className={styles.input}
                      value={brand}
                      disabled={!isPending}
                      placeholder="e.g. Malvern or [Brand] in email subject"
                      onChange={(e) => setBrand(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <Text variant="h5">Insight body</Text>
                    <textarea
                      className={styles.textarea}
                      value={bodyText}
                      disabled={!isPending}
                      onChange={(e) => setBodyText(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <Text variant="h5">Reviewer note</Text>
                    <input
                      className={styles.input}
                      value={reviewNote}
                      disabled={!isPending && selected.status !== "rejected"}
                      onChange={(e) => setReviewNote(e.target.value)}
                    />
                  </div>

                  {!!selected.attachments?.length && (
                    <div className={styles.attachments}>
                      <Text variant="h5">Attachments</Text>
                      {selected.attachments.map((file) => (
                        <Text
                          key={`${file.name}-${file.blobUrl || file.size}`}
                          variant="caption1"
                          className={styles.attachment}
                        >
                          {file.name}
                          {file.size ? ` (${Math.round(file.size / 1024)} KB)` : ""}
                        </Text>
                      ))}
                    </div>
                  )}

                  <div className={styles.actions}>
                    {isPending && access.canUpdateExpertArticleInsights && (
                      <Button
                        color="default"
                        variant="outlined"
                        onClick={handleSave}
                      >
                        Save edits
                      </Button>
                    )}
                    {isPending && access.canCreateExpertArticleInsights && (
                      <Button color="primary" onClick={handleApprove}>
                        Approve & publish
                      </Button>
                    )}
                    {isPending && access.canUpdateExpertArticleInsights && (
                      <Button
                        color="default"
                        variant="outlined"
                        onClick={handleReject}
                      >
                        Reject
                      </Button>
                    )}
                    {selected.status !== "archived" &&
                      access.canUpdateExpertArticleInsights && (
                        <Button
                          color="default"
                          variant="outlined"
                          onClick={handleArchive}
                        >
                          Archive
                        </Button>
                      )}
                  </div>
                </div>
              ) : (
                <NotFound>No submission selected</NotFound>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  )
})

export default EmailKnowledgePage
